import React, { useEffect, useState } from "react";
//#region types
import {
  FormHandles,
  FormItemDefinitionType,
  GenericFormField,
  GenericFormSection,
} from "models/propTypes/genericFormProps";
import { StyleEnums } from "models/enums";
import { FilterDrawerProps } from "models/propTypes/filterPropTypes";
import { IconDefinitionConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelCustomFilters } from "wint-api-client/dist/types";
//#endregion
//#region components
import useFilterContext from "./context/useFilterContext";
import useGetHasValueCount from "../forms/hooks/useGetHasValueCount";
import usePrevious from "../usePrevious";
import Icon from "../icon";
import FilterMoreActions from "./moreActions";
import FilterFormContainer from "./forms/filterFormContainer";
import PageFooter from "../pageFooter";
import { Button, Drawer, Grid } from "@mui/material";
import { useFilterService } from "serviceHooks";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";
import { useDispatch } from "react-redux";
import { setFilterDrawerOpen } from "store/actions/utilActions";

function FilterDrawer<T extends Record<string, any>>(props: FilterDrawerProps<T>) {
  const filterService = useFilterService();
  const filterFormRef = React.useRef<FormHandles>(null);

  const dispatch = useDispatch();

  const {
    isOpen,
    setFilter,
    toggleFilter,
    filter,
    setFilterCount,
    filterCategory,
    reportToAPIFilterFormatter,
    apiToReportFilterFormatter,
    hasUserChosenFilter,
  } = useFilterContext<T>();

  const [savedFilters, setSavedFilters] = useState<WintSuperkollAPIModelCustomFilters.CustomFilter[]>([]);
  const [currentSavedFilter, setCurrentSavedFilter] = useState<WintSuperkollAPIModelCustomFilters.CustomFilter>();

  const { getHasValueCount } = useGetHasValueCount(filter);

  const actualReportToAPIFilterFormatter = reportToAPIFilterFormatter ?? ((filter) => filter);
  const actualAPIToReportFilterFormatter = apiToReportFilterFormatter ?? ((filter) => Promise.resolve(filter));

  React.useEffect(() => {
    dispatch(setFilterDrawerOpen(isOpen));
  }, [isOpen]);

  const loadFilters = async (selectFilterIdOnLoad?: number) => {
    if (filterCategory === undefined) return [];
    const filters = await filterService.loadFiltersOfType(filterCategory);
    setSavedFilters(filters ?? []);
    if (selectFilterIdOnLoad !== undefined) {
      setSelectedFilter(filters, selectFilterIdOnLoad);
    }
  };

  const setSelectedFilter = (filters: WintSuperkollAPIModelCustomFilters.CustomFilter[], selectedFilterId?: number) => {
    updateCurrentQuickSelection(selectedFilterId, filters);
    if (selectedFilterId === undefined) {
      setFilter({} as T);
      return;
    }

    const filter = filters.find((f) => f.id === selectedFilterId);
    if (filter) {
      actualAPIToReportFilterFormatter(filter.filterData).then((reportFilter) => {
        reportFilter.selectedFilter = selectedFilterId;
        setFilter(reportFilter);
      });
    }
  };

  const updateCurrentQuickSelection = (
    filterId?: number,
    filters?: WintSuperkollAPIModelCustomFilters.CustomFilter[]
  ) => {
    const currentFilters = filters ?? savedFilters;
    const filter = currentFilters?.find((f) => f.id === filterId);
    setCurrentSavedFilter(filter);
  };

  const onSelectedFilterUpdateSuccess = (
    callback: (
      filters: WintSuperkollAPIModelCustomFilters.CustomFilter[]
    ) => WintSuperkollAPIModelCustomFilters.CustomFilter[]
  ) => {
    setSavedFilters(callback(savedFilters));
  };

  const getFilterValues = () => {
    if (!filterFormRef?.current?.getValues) {
      return;
    }

    return actualReportToAPIFilterFormatter(filterFormRef.current.getValues());
  };

  const previousCategory = usePrevious(filterCategory);

  useEffect(() => {
    if (previousCategory !== undefined && previousCategory !== filterCategory) {
      setSelectedFilter(savedFilters);
      setSavedFilters([]);
    }

    loadFilters();
  }, [filterCategory]);

  useEffect(() => {
    if (filter?.selectedFilter !== undefined) {
      updateCurrentQuickSelection(filter.selectedFilter);
    } else if (!hasUserChosenFilter()) {
      const defaultFilter_ = savedFilters?.find((f) => f.isDefault);
      if (defaultFilter_ !== undefined) {
        setSelectedFilter(savedFilters, defaultFilter_.id);
      }
    }
  }, [savedFilters]);

  React.useEffect(() => {
    setSelectedValueCounts();

    if (filter?.selectedFilter !== currentSavedFilter?.id) {
      updateCurrentQuickSelection(filter?.selectedFilter);
    }
  }, [filter]);

  const setSelectedValueCounts = () => {
    const count = countFieldsWithValues(props.fields);
    setFilterCount(count);
  };

  const countFieldsWithValues = (fields: FormItemDefinitionType<T>): number => {
    if (Array.isArray(fields)) {
      const childSelected = fields.flatMap((field: GenericFormSection<T>) => countFieldsWithValues(field.innerFields));
      return childSelected.reduce((total: number, fieldValue) => total + fieldValue, 0);
    } else {
      return fields
        ? Object.entries(fields).reduce((total: number, currentField) => {
            const [key, item] = currentField;
            const field = item as GenericFormField<Record<string, any>>;
            const fieldFilledCount = field ? getHasValueCount(field, key) : 0;
            return total + fieldFilledCount;
          }, 0)
        : 0;
    }
  };

  const onFilter = async () => {
    if (filterFormRef.current?.getValues) {
      setFilter(filterFormRef.current?.getValues());
    }
    toggleFilter();
  };

  const onResetButtonClick = async () => {
    setFilter({} as T);
    setSelectedFilter(savedFilters);
    toggleFilter();
  };

  return (
    <Drawer
      anchor={"right"}
      open={isOpen}
      transitionDuration={500}
      onClose={onFilter}
      classes={{ root: cssExports.filterDrawer, paper: cssExports.filterDrawerContainer }}
      BackdropProps={{ className: cssExports.filterDrawerBackdrop }}
    >
      <Button className={cssExports.drawerCloseIcon} onClick={onFilter} disableRipple>
        <Icon icon={IconDefinitionConstants.WTimesThin} />
      </Button>
      {filterCategory ? (
        <FilterMoreActions
          selectedFilter={currentSavedFilter}
          filterCategory={filterCategory}
          onSelectedFilterUpdateSuccess={onSelectedFilterUpdateSuccess}
          getFilterValues={getFilterValues}
          loadFilters={loadFilters}
        />
      ) : (
        <></>
      )}
      <Grid container justifyContent="center">
        <Grid item className={cssExports.smallTitle}>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Filter)}
        </Grid>
      </Grid>
      <Grid
        container
        direction="column"
        justifyContent="space-between"
        className={cssExports.filterContainer}
        wrap="nowrap"
      >
        <Grid container className={cssExports.filterFormContainer}>
          <FilterFormContainer
            ref={filterFormRef}
            fields={props.fields}
            filter={filter}
            savedFilters={savedFilters}
            filterCategory={filterCategory}
            onQuickSelectionChange={setSelectedFilter.bind(null, savedFilters)}
            validationSchema={props.validationSchema}
          />
        </Grid>
        <Grid container className={cssExports.footerContainer}>
          <PageFooter
            actions={[
              {
                text: TranslationKeys.Common.Reset,
                onClick: onResetButtonClick,
                secondary: true,
              },
              {
                text: TranslationKeys.Common.FilterAction,
                onClick: onFilter,
                type: StyleEnums.DisplayType.Info,
              },
            ]}
            allowForReadOnlyUsers
          />
        </Grid>
      </Grid>
    </Drawer>
  );
}

export default FilterDrawer;
